import { i18n } from "@tellescope/i18n";
import { find_and_parse_json_output, extract_array_from_json, validate_tool_selection } from "./parsing";
import { build_tool_selection_prompt, JSON_RESPONSE_SYSTEM_PROMPT } from "./prompt_utils";
import { Tool, ToolResult, ToolSelection } from "./tools";
import { AgentError } from "./types";

export type AIMessage = {
  role: "user" | "assistant";
  text: string;
  timestamp: Date;
};

export type SendMessageOptions = {
  messages: AIMessage[]; // Structured conversation history (pass singleton for single message)
  prompt?: string | undefined;
  type?: string | undefined;
  orchestrationId?: string | undefined; // to group related conversations within an orchestration run
  maxTokens?: number | undefined;
};

export interface Agent {
  name: string;
  selectionPrompt: string; // During intent detection, used to select relevant agents
  state: "Idle" | "Planning" | "Executing";
  tools: Tool[];
  getCompletion: (v: SendMessageOptions) => Promise<{ response: string }>;
  results: ToolResult[];
  errors: AgentError[];
  selectedTools: ToolSelection[];
}

export const create_agent = ({
  name,
  selectionPrompt,
  tools,
  getCompletion,
}: {
  tools: Tool[];
  name: string;
  selectionPrompt: string;
  getCompletion: Agent["getCompletion"];
}): Agent => {
  return {
    name,
    selectionPrompt,
    state: "Idle",
    tools,
    getCompletion,
    results: [],
    errors: [],
    selectedTools: [],
  };
};

type AgentLoopTransitionType = "Selected Tools" | "Executing Tool" | "Success";
export const run_agent_loop = async (
  agent: Agent,
  messages: AIMessage[],
  o?: {
    initialState?: Agent["state"];
    onChange?: (agent: Agent, type: AgentLoopTransitionType) => void;
    onError?: (agent: Agent, error: AgentError) => void;
    orchestrationId?: string;
  }
) => {
  const { onChange, onError, orchestrationId, initialState = "Planning" } = o || {};

  // Create fresh results/errors arrays to avoid accumulating state across multiple runs
  // Note: selectedTools is preserved as it may be pre-populated for "Executing" state
  let currentAgent: Agent = {
    ...agent,
    state: initialState,
    results: [],
    errors: [],
  };

  const MAX_ITERATIONS = 100;
  let iterations = 0;

  while (currentAgent.state !== "Idle") {
    if (++iterations > MAX_ITERATIONS) {
      const agentError: AgentError = {
        message: i18n.t("Agent loop exceeded maximum iterations ({{amount}})", {
          ns: "ai-agent",
          amount: MAX_ITERATIONS,
        }),
      };
      currentAgent.errors.push(agentError);
      onError?.(currentAgent, agentError);
      break;
    }
    if (currentAgent.state === "Planning") {
      // can bypass tool selection if only 1 tool
      if (agent.tools.length === 1) {
        // Use the last user message as instructions since we're skipping tool selection
        const lastUserMessage = [...messages].reverse().find((m) => m.role === "user");
        const onlyTool = agent.tools[0];
        if (!onlyTool) break;
        currentAgent.selectedTools = [{ toolName: onlyTool.name, instructions: lastUserMessage?.text || "" }];
        currentAgent.state = "Executing";
        onChange?.(currentAgent, "Selected Tools");
        continue;
      }

      // Tool selection logic
      try {
        const { response } = await agent.getCompletion({
          prompt: JSON_RESPONSE_SYSTEM_PROMPT + "\n" + build_tool_selection_prompt(agent.tools),
          type: "tool-selection",
          messages,
          orchestrationId,
        });

        const parsedJson = find_and_parse_json_output(response);
        const toolSelectionArray = extract_array_from_json(parsedJson);
        const selection = validate_tool_selection(toolSelectionArray, agent.tools);

        // handles both no JSON in response or invalid JSON shape for tool selection
        if (selection.length === 0) {
          const agentError: AgentError = {
            message: i18n.t("Failed to parse tool selection from response", { ns: "ai-agent" }),
            rawResponse: response,
            context: "tool-selection",
          };
          currentAgent.errors.push(agentError);
          onError?.(currentAgent, agentError);
          currentAgent.state = "Idle";
          continue;
        }

        currentAgent.selectedTools = selection;
        currentAgent.state = "Executing";
        onChange?.(currentAgent, "Selected Tools");
      } catch (error) {
        const agentError: AgentError = { message: (error as Error)?.message };
        currentAgent.errors.push(agentError);
        onError?.(currentAgent, agentError);
        currentAgent.state = "Idle";
      }
    }

    if (currentAgent.state === "Executing") {
      if (currentAgent.selectedTools.length === 0) {
        currentAgent.state = "Idle";
        onChange?.(currentAgent, "Success");
        continue;
      }

      const toolSelection = currentAgent.selectedTools.shift()!;
      const tool = currentAgent.tools.find((t) => t.name === toolSelection.toolName);

      if (!tool) {
        continue;
      } // should not happen due to prior validation, but good safeguard

      try {
        const { response } = await agent.getCompletion({
          prompt: tool.executionPrompt,
          type: "tool-execution",
          messages: [
            ...currentAgent.results.map((r) => ({
              role: "assistant" as const,
              text: r.summarizedOutput,
              timestamp: r.timestamp,
            })),
            { role: "user", text: toolSelection.instructions, timestamp: new Date() },
          ],
          orchestrationId,
        });

        const toolInput = find_and_parse_json_output(response);

        if (!toolInput) {
          const agentError: AgentError = {
            message: i18n.t("Failed to parse tool input for {{toolName}}", { ns: "ai-agent", toolName: tool.name }),
            rawResponse: response,
            context: "tool-execution",
          };
          currentAgent.errors.push(agentError);
          onError?.(currentAgent, agentError);
          currentAgent.state = "Idle";
          continue;
        }

        const validationErrors = tool.validateInput(toolInput);
        if (validationErrors.length > 0) {
          const agentError: AgentError = {
            message: i18n.t("Tool input validation failed for {{toolName}}: {{errors}}", {
              ns: "ai-agent",
              toolName: tool.name,
              errors: validationErrors.join(", "),
            }),
          };
          currentAgent.errors.push(agentError);
          onError?.(currentAgent, agentError);
          currentAgent.state = "Idle";
          continue;
        }

        const toolResult = await tool.execute(toolInput);
        currentAgent.results.push({ ...toolResult, toolName: tool.name, timestamp: new Date() });

        if (currentAgent.selectedTools.length === 0) {
          currentAgent.state = "Idle";
          onChange?.(currentAgent, "Success");
          continue;
        }

        onChange?.(currentAgent, "Executing Tool");
      } catch (error) {
        const agentError: AgentError = { message: (error as Error)?.message };
        currentAgent.errors.push(agentError);
        onError?.(currentAgent, agentError);
        currentAgent.state = "Idle";
        continue;
      }
    }
  }
};
